import {
  LEDModes,
  MachinePublicHydrated,
} from "@aura-frontend/omnibus-az-client";
import { useState } from "react";

import { EventLEDBoardConfiguration } from "@/components/EventForm/EventLEDBoardConfiguration/EventLEDBoardConfiguration";
import { useMachinesAsOf } from "@/hooks/useComplexes";
import { getMachineHealth } from "@/utils/machineHealth";

import { DeactivateLEDBoardDialog } from "../../SingleComplex/DeactivateLEDBoardDialog";
import { DeactivateParksDialog } from "../../SingleComplex/DeactivateParksDialog";
import { DeactivateUmpireDialog } from "../../SingleComplex/DeactivateUmpireDialog";
import { EnableLEDBoardDialog } from "./EnableLEDBoardDialog";

type LEDBoardConfigurationProps = {
  field: MachinePublicHydrated;
  // Single-field page also fetches /unified for the same machine. When present,
  // its embedded `machine` is fresher than the search-list `field` and is used
  // as the just-in-time source for health and entitlement state.
  unifiedMachine?: MachinePublicHydrated;
  pendingMode: LEDModes | null;
  committedMode: LEDModes | null;
  // End time currently scheduled on the backend for the committed mode. Used to
  // preselect the picker when the user re-clicks the committed mode.
  committedEndTimeISO: string | null;
  onStageChange: (
    mode: LEDModes | null,
    endTimeISO: string | null,
    orgId: string | null,
  ) => void;
};

type ActiveDialog =
  | { kind: "enable"; target: Exclude<LEDModes, never> }
  | { kind: "deactivate-parks" }
  | { kind: "deactivate-umpire" }
  | { kind: "deactivate-led" }
  | null;

function LEDBoardConfiguration({
  field,
  unifiedMachine,
  pendingMode,
  committedMode,
  committedEndTimeISO,
  onStageChange,
}: LEDBoardConfigurationProps) {
  const liveField = unifiedMachine ?? field;
  const asOfMs = useMachinesAsOf();
  const health = getMachineHealth(liveField, asOfMs);

  const [activeDialog, setActiveDialog] = useState<ActiveDialog>(null);
  const [dialogTarget, setDialogTarget] = useState<LEDModes | null>(null);

  const resolveDialog = (
    from: LEDModes | null,
    to: LEDModes | null,
  ): ActiveDialog => {
    if (from === to) return null;
    if (to === null) {
      if (from === "blocked") return { kind: "deactivate-led" };
      // from === "stat_tracking" or "umpire_assist": removing all stat-based
      // entitlements is the "Deactivate SmartPark" flow.
      return { kind: "deactivate-parks" };
    }
    // Only-removing-umpire is the one partial-deactivate transition.
    if (from === "umpire_assist" && to === "stat_tracking") {
      return { kind: "deactivate-umpire" };
    }
    return { kind: "enable", target: to };
  };

  const handleRadioChange = (next: LEDModes | null) => {
    if (next === committedMode) {
      // Re-clicking the committed mode while a different mode is staged. For
      // "off", just revert silently — there's no time to confirm. Otherwise
      // open the enable dialog with the existing schedule preselected so the
      // user can confirm the time or pick a new one.
      if (committedMode === null) {
        onStageChange(committedMode, null, null);
        return;
      }
      setDialogTarget(committedMode);
      setActiveDialog({ kind: "enable", target: committedMode });
      return;
    }
    setDialogTarget(next);
    setActiveDialog(resolveDialog(committedMode, next));
  };

  const closeDialog = () => {
    setActiveDialog(null);
    setDialogTarget(null);
  };

  const confirmEnable = (endTimeISO: string, orgId: string | null) => {
    if (dialogTarget === null) return;
    onStageChange(dialogTarget, endTimeISO, orgId);
    closeDialog();
  };

  const confirmDeactivate = (endTimeISO: string | null) => {
    onStageChange(dialogTarget, endTimeISO, null);
    closeDialog();
  };

  return (
    <div>
      <EventLEDBoardConfiguration
        isFieldHealthy={health.state === "green"}
        value={pendingMode}
        onValueChange={handleRadioChange}
      />

      <EnableLEDBoardDialog
        isOpen={activeDialog?.kind === "enable"}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
        targetMode={
          activeDialog?.kind === "enable" ? activeDialog.target : null
        }
        initialEndTimeISO={
          activeDialog?.kind === "enable" &&
          activeDialog.target === committedMode
            ? committedEndTimeISO
            : null
        }
        machineUuid={liveField.machineUuid ?? ""}
        isSubmitting={false}
        onConfirm={confirmEnable}
      />

      <DeactivateParksDialog
        isOpen={activeDialog?.kind === "deactivate-parks"}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
        isSubmitting={false}
        onConfirm={() => confirmDeactivate(null)}
      />

      <DeactivateUmpireDialog
        isOpen={activeDialog?.kind === "deactivate-umpire"}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
        isSubmitting={false}
        onConfirm={() =>
          setActiveDialog({ kind: "enable", target: "stat_tracking" })
        }
      />

      <DeactivateLEDBoardDialog
        isOpen={activeDialog?.kind === "deactivate-led"}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
        isSubmitting={false}
        onConfirm={() => confirmDeactivate(null)}
      />
    </div>
  );
}

export { LEDBoardConfiguration };
